import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/config";
import { Stars } from "@/components/Stars";
import { toast } from "sonner";
import { Loader2, LogOut, Check, X, Trash2, Mail, Plus } from "lucide-react";

const ROLE_OPTIONS = ["Parent", "Teacher", "Student"];

const TOKEN_KEY = "elite_admin_token";

const Login = ({ onAuth }) => {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/admin/verify`, { token });
      localStorage.setItem(TOKEN_KEY, token);
      onAuth(token);
      toast.success("Signed in.");
    } catch {
      toast.error("Invalid token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FCFCFC] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md border border-[#EAEAEA] bg-white p-10"
      >
        <div className="overline">Consultant</div>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-4">
          Admin sign-in
        </h1>
        <p className="mt-3 text-sm text-[#4A4A52]">
          Enter your admin token to moderate reviews and view enquiries.
        </p>
        <label className="text-xs uppercase tracking-[0.2em] text-[#4A4A52] mt-8 block">Token</label>
        <input
          data-testid="admin-token-input"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="mt-2 w-full border border-[#EAEAEA] px-4 py-3 text-sm focus:outline-none focus:border-[#0A192F]"
          required
        />
        <button
          disabled={busy}
          data-testid="admin-signin-btn"
          className="mt-6 w-full bg-[#0A192F] hover:bg-[#172A4A] text-white text-sm font-semibold px-6 py-4 transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
        >
          {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : "Sign in"}
        </button>
        <a href="/" className="mt-6 block text-xs uppercase tracking-[0.2em] text-[#4A4A52] hover:text-[#0A192F]">
          ← Back to site
        </a>
      </form>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rejected: "bg-neutral-100 text-neutral-500 border-neutral-200",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.18em] border px-2 py-1 ${map[status] || ""}`}>
      {status}
    </span>
  );
};

const AdminInner = ({ token, onLogout }) => {
  const [tab, setTab] = useState("approved");
  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newReview, setNewReview] = useState({ name: "", roles: [], rating: 5, content: "" });
  const [savingNew, setSavingNew] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [rev, cts] = await Promise.all([
        axios.get(`${API_BASE}/admin/reviews`, authHeader),
        axios.get(`${API_BASE}/admin/contact`, authHeader),
      ]);
      setReviews(rev.data);
      setContacts(cts.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        onLogout();
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleNewRole = (r) =>
    setNewReview((f) => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
    }));

  const createReview = async (e) => {
    e.preventDefault();
    if (!newReview.name.trim() || newReview.content.trim().length < 10) {
      toast.error("Please add a name and a review of at least 10 characters.");
      return;
    }
    setSavingNew(true);
    try {
      const role = ROLE_OPTIONS.filter((r) => newReview.roles.includes(r)).join(" & ");
      await axios.post(
        `${API_BASE}/admin/reviews`,
        { name: newReview.name, role, rating: newReview.rating, content: newReview.content },
        authHeader
      );
      toast.success("Review added and published.");
      setNewReview({ name: "", roles: [], rating: 5, content: "" });
      setShowAdd(false);
      setTab("approved");
      load();
    } catch {
      toast.error("Could not create review.");
    } finally {
      setSavingNew(false);
    }
  };

  const act = async (id, action) => {
    try {
      if (action === "delete") {
        await axios.delete(`${API_BASE}/admin/reviews/${id}`, authHeader);
      } else {
        await axios.post(`${API_BASE}/admin/reviews/${id}/${action}`, {}, authHeader);
      }
      toast.success(`Review ${action}d.`);
      load();
    } catch {
      toast.error("Action failed.");
    }
  };

  const filtered = reviews.filter((r) => (tab === "all" ? true : r.status === tab));

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <header className="border-b border-[#EAEAEA] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="overline">Admin</div>
            <div className="font-display text-lg font-medium">Console</div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs uppercase tracking-[0.2em] text-[#4A4A52] hover:text-[#0A192F]">View site →</a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A4A52] hover:text-[#0A192F]"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            {[
              { k: "approved", label: `Approved (${reviews.filter(r=>r.status==='approved').length})` },
              { k: "pending", label: `Pending (${reviews.filter(r=>r.status==='pending').length})` },
              { k: "rejected", label: `Rejected (${reviews.filter(r=>r.status==='rejected').length})` },
              { k: "all", label: `All (${reviews.length})` },
              { k: "contacts", label: `Enquiries (${contacts.length})` },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`admin-tab-${t.k}`}
                className={`text-xs uppercase tracking-[0.18em] px-4 py-2 border transition-colors ${
                  tab === t.k
                    ? "bg-[#0A192F] text-white border-[#0A192F]"
                    : "bg-white text-[#4A4A52] border-[#EAEAEA] hover:border-[#0A192F]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            data-testid="admin-add-review-btn"
            className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#172A4A] text-white text-xs uppercase tracking-[0.18em] px-4 py-2"
          >
            <Plus size={14} /> {showAdd ? "Cancel" : "Add review"}
          </button>
        </div>

        {showAdd && (
          <form
            onSubmit={createReview}
            data-testid="admin-add-review-form"
            className="border border-[#EAEAEA] bg-white p-6 md:p-8 mb-8 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#4A4A52]">Client name *</label>
                <input
                  data-testid="admin-new-name"
                  className="mt-2 w-full border border-[#EAEAEA] px-4 py-3 text-sm focus:outline-none focus:border-[#0A192F]"
                  value={newReview.name}
                  onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                  maxLength={100}
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#4A4A52]">
                  Client role
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map((r) => {
                    const active = newReview.roles.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => toggleNewRole(r)}
                        data-testid={`admin-new-role-${r.toLowerCase()}`}
                        className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                          active
                            ? "bg-[#0A192F] text-white border-[#0A192F]"
                            : "bg-white text-[#4A4A52] border-[#EAEAEA] hover:border-[#0A192F]"
                        }`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#4A4A52]">Rating</label>
              <div className="mt-2 flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNewReview({ ...newReview, rating: n })}
                    className={`w-9 h-9 border text-sm font-semibold transition-colors ${
                      newReview.rating >= n
                        ? "bg-[#0A192F] text-white border-[#0A192F]"
                        : "bg-white text-[#4A4A52] border-[#EAEAEA] hover:border-[#0A192F]"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#4A4A52]">Review text *</label>
              <textarea
                data-testid="admin-new-content"
                className="mt-2 w-full border border-[#EAEAEA] px-4 py-3 text-sm focus:outline-none focus:border-[#0A192F] min-h-[140px] resize-y"
                value={newReview.content}
                onChange={(e) => setNewReview({ ...newReview, content: e.target.value })}
                minLength={10}
                maxLength={2000}
                required
              />
            </div>
            <button
              type="submit"
              disabled={savingNew}
              data-testid="admin-new-submit"
              className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#172A4A] text-white text-sm font-semibold px-6 py-3 disabled:opacity-70"
            >
              {savingNew ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Publish review"}
            </button>
            <div className="text-xs text-[#4A4A52]">
              Reviews added here are published immediately.
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-sm text-[#4A4A52]">Loading…</div>
        ) : tab === "contacts" ? (
          <div className="space-y-4" data-testid="admin-contacts">
            {contacts.length === 0 && <div className="text-sm text-[#4A4A52]">No enquiries yet.</div>}
            {contacts.map((c) => (
              <div key={c.id} className="border border-[#EAEAEA] bg-white p-6">
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-medium">{c.name}</div>
                    <a href={`mailto:${c.email}`} className="text-sm text-[#0A192F] inline-flex items-center gap-1">
                      <Mail size={12} /> {c.email}
                    </a>
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#4A4A52]">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                {c.subject && <div className="mt-4 text-sm font-semibold">{c.subject}</div>}
                <p className="mt-2 text-sm text-[#0A0A0B] whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-reviews-list">
            {filtered.length === 0 && <div className="text-sm text-[#4A4A52]">Nothing here.</div>}
            {filtered.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-[#EAEAEA] bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="font-display text-lg font-medium">{r.name}</div>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#4A4A52] mt-1">
                      {r.role || "Client"} · {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Stars value={r.rating} />
                </div>
                <p className="mt-4 text-sm text-[#0A0A0B] whitespace-pre-wrap">{r.content}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {r.status !== "approved" && (
                    <button
                      onClick={() => act(r.id, "approve")}
                      data-testid={`approve-${r.id}`}
                      className="inline-flex items-center gap-2 bg-[#0A192F] text-white text-xs uppercase tracking-[0.18em] px-4 py-2 hover:bg-[#172A4A]"
                    >
                      <Check size={14} /> Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button
                      onClick={() => act(r.id, "reject")}
                      data-testid={`reject-${r.id}`}
                      className="inline-flex items-center gap-2 border border-[#EAEAEA] text-[#4A4A52] text-xs uppercase tracking-[0.18em] px-4 py-2 hover:border-[#0A192F] hover:text-[#0A192F]"
                    >
                      <X size={14} /> Reject
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm("Delete this review permanently?")) act(r.id, "delete");
                    }}
                    data-testid={`delete-${r.id}`}
                    className="inline-flex items-center gap-2 border border-transparent text-red-700 text-xs uppercase tracking-[0.18em] px-4 py-2 hover:border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };
  return token ? <AdminInner token={token} onLogout={logout} /> : <Login onAuth={setToken} />;
}
